import { CloseIcon } from '@chakra-ui/icons';
import {
  Box,
  Input,
  Tag,
  Wrap,
  Text,
  VStack,
  IconButton,
  Flex,
  Button,
} from '@chakra-ui/react';
import React, { useState, useRef, useEffect } from 'react';

import {
  generateTags,
  TAG,
  toggleTag,
  useGraph,
  clearTags,
} from './GraphContext';

type Props = { onClose: () => void };
export const SearchOverlay = ({ onClose }: Props) => {
  const [inputValue, setInputValue] = useState('');

  const [showAllTags, setShowAllTags] = useState(false);
  const graphContext = useGraph();
  const selectedTags = graphContext.state.selectedTags;
  const tags = generateTags(graphContext);
  const dialogBoxRef = useRef<HTMLDivElement>(null); // Ref for the dialog box

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dialogBoxRef.current &&
        !dialogBoxRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Attach the listener to the document
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      // Remove the listener on cleanup
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleTagClick = (tag: TAG) => {
    toggleTag(graphContext, tag);
  };

  const handleEscape = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Escape') {
      onClose();
    }

    // also handle ctrl+/ to close the panel
    if (event.key === '/' && event.ctrlKey) {
      onClose();
    }
  };

  const filteredTags = showAllTags
    ? tags
    : tags.filter(
        (tag) =>
          inputValue !== '' &&
          tag.tag.toLowerCase().includes(inputValue.toLowerCase()),
      );

  const filteredTagStringSet = new Set(filteredTags.map((tag) => tag.tag));
  selectedTags.forEach((tag) => {
    if (!filteredTagStringSet.has(tag.tag)) {
      filteredTags.push(tag);
    }
  });

  const tagTypeToTags = new Map<string, string[]>();
  filteredTags.forEach((tag) => {
    const tagsOfType = tagTypeToTags.get(tag.type) || [];
    tagsOfType.push(tag.tag);
    tagTypeToTags.set(tag.type, tagsOfType);
  });

  const tagTypeToSelectedTags = new Map<string, Set<string>>();
  selectedTags.forEach((tag) => {
    const tagsOfType = tagTypeToSelectedTags.get(tag.type) || new Set<string>();
    tagsOfType.add(tag.tag);
    tagTypeToSelectedTags.set(tag.type, tagsOfType);
  });

  const tagTypes = Array.from(tagTypeToTags.keys());

  const handleShowAll = () => {
    setShowAllTags(!showAllTags);
  };

  const handleClear = () => {
    clearTags(graphContext);
  };

  return (
    <Box
      ref={dialogBoxRef}
      position="fixed"
      top="10%" // Adjusted to ensure the dialog is not clipped at the top
      left="50%"
      transform="translateX(-50%)" // Only shifting horizontally
      width="50vw"
      maxWidth="500px"
      maxHeight="80vh" // Keeping the max height but ensuring it's responsive
      overflowY="auto" // Making content scrollable
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start" // Adjusted to align content from the top
      backgroundColor="rgba(0, 0, 0, 0.5)"
      zIndex="modal"
      p={4}
      borderRadius="md" // Optional: Adding some border radius for aesthetics
    >
      <VStack width="100%" align="stretch" spacing={1}>
        <Flex justifyContent="space-between" mb={1}>
          <Button
            variant="link"
            fontSize="xs"
            color="blue.500"
            onClick={handleShowAll}
          >
            {showAllTags ? 'show filtered Tags' : 'show all tags'}
          </Button>
          {selectedTags.length > 0 && (
            <Button
              variant="link"
              fontSize="xs"
              color="blue.500"
              onClick={handleClear}
            >
              remove filters
            </Button>
          )}
        </Flex>

        <Flex justifyContent="space-between" alignItems="center" width="100%">
          <Flex flexDirection="column" flex="1">
            <Input
              placeholder="Search..."
              value={inputValue}
              onChange={handleChange}
              onKeyDown={handleEscape}
              size="lg"
              autoFocus
            />
          </Flex>
          <IconButton
            aria-label="Close search overlay"
            icon={<CloseIcon />}
            ml={2}
            onClick={onClose}
          />
        </Flex>
      </VStack>
      <VStack
        mt={2}
        spacing="10px"
        alignContent="left"
        alignItems="left"
        width="100%"
      >
        {tagTypes.map((tagType) => (
          <Box key={tagType}>
            <Text>{tagType}</Text>
            <Wrap key={tagType} mt={2} spacing="10px">
              {tagTypeToTags.get(tagType)?.map((tag) => (
                <Tag
                  key={tag}
                  cursor="pointer"
                  variant={
                    tagTypeToSelectedTags.get(tagType)?.has(tag)
                      ? 'solid'
                      : 'outline'
                  }
                  colorScheme={
                    tagTypeToSelectedTags.get(tagType)?.has(tag)
                      ? 'teal'
                      : 'gray'
                  }
                  // @ts-ignore: tagType is TagType, but it comes as string :) here.
                  onClick={() => handleTagClick({ type: tagType, tag })}
                >
                  {tag}
                </Tag>
              ))}
            </Wrap>
          </Box>
        ))}
      </VStack>
    </Box>
  );
};
